import { compileMap } from '../autotile.js';
import type { MapDef, MapSource } from '../schemas/defs.js';

/**
 * The three authored maps (GDD §4).
 *
 * All three share one tileset and one palette — v1 ships a single biome (TRD-12 A-15).
 * They differ in *layout*, which is what players actually engage with.
 *
 * Path length is the balance lever that matters most: it is directly proportional to total
 * tower uptime, and therefore to how hard the map is (TRD-13 R-13-08). The generator
 * targets 45-70 tiles, and these are authored into the same band so authored and generated
 * maps are comparable.
 */

export const MAP_SOURCES: MapSource[] = [
  {
    id: 'the-furrow',
    name: 'The Furrow',
    biome: 'ashen-flat',
    // ~63 tiles. Long serpentine with generous build room on both sides — the
    // tutorial-grade map, where almost any placement works and the synergy web is the
    // only thing being tested.
    paths: [
      {
        id: 0,
        waypoints: [
          { x: 0, y: 9 },
          { x: 6, y: 9 },
          { x: 6, y: 3 },
          { x: 12, y: 3 },
          { x: 12, y: 14 },
          { x: 18, y: 14 },
          { x: 18, y: 3 },
          { x: 24, y: 3 },
          { x: 24, y: 9 },
          { x: 29, y: 9 },
        ],
      },
    ],
    core: { x: 29, y: 9 },
    blockers: [
      { x0: 2, y0: 0, x1: 4, y1: 1 },
      { x0: 8, y0: 16, x1: 10, y1: 17 },
      { x0: 20, y0: 16, x1: 22, y1: 17 },
      { x0: 26, y0: 0, x1: 28, y1: 1 },
      { x0: 15, y0: 7, x1: 16, y1: 10 },
    ],
    modifier: 'none',
  },

  {
    id: 'split-vein',
    name: 'Split Vein',
    biome: 'ashen-flat',
    // ~45 tiles per lane. Two mirrored routes that converge only near the Core, so half
    // your board idles each wave unless you commit to the convergence zone. Lanes stay
    // ≥ 4 tiles apart outside it (TRD-13 R-13-12).
    paths: [
      {
        id: 0,
        waypoints: [
          { x: 0, y: 2 },
          { x: 6, y: 2 },
          { x: 6, y: 7 },
          { x: 14, y: 7 },
          { x: 14, y: 2 },
          { x: 22, y: 2 },
          { x: 22, y: 7 },
          { x: 26, y: 9 },
          { x: 29, y: 9 },
        ],
      },
      {
        id: 1,
        waypoints: [
          { x: 0, y: 16 },
          { x: 6, y: 16 },
          { x: 6, y: 11 },
          { x: 14, y: 11 },
          { x: 14, y: 16 },
          { x: 22, y: 16 },
          { x: 22, y: 11 },
          { x: 26, y: 9 },
          { x: 29, y: 9 },
        ],
      },
    ],
    core: { x: 29, y: 9 },
    blockers: [
      { x0: 9, y0: 0, x1: 11, y1: 0 },
      { x0: 9, y0: 17, x1: 11, y1: 17 },
      { x0: 17, y0: 8, x1: 19, y1: 10 },
    ],
    modifier: 'alternating-lanes',
  },

  {
    id: 'the-coil',
    name: 'The Coil',
    biome: 'ashen-flat',
    // ~44 tiles, the shortest of the three, spiralling to a central Core. Short path means
    // low total uptime: a hard DPS check where slows buy much less than they do elsewhere.
    paths: [
      {
        id: 0,
        waypoints: [
          { x: 0, y: 9 },
          { x: 8, y: 9 },
          { x: 8, y: 5 },
          { x: 20, y: 5 },
          { x: 20, y: 13 },
          { x: 14, y: 13 },
          { x: 14, y: 9 },
          { x: 16, y: 9 },
        ],
      },
    ],
    core: { x: 16, y: 9 },
    blockers: [
      { x0: 0, y0: 0, x1: 31, y1: 1 },
      { x0: 0, y0: 16, x1: 31, y1: 17 },
      { x0: 25, y0: 7, x1: 27, y1: 11 },
    ],
    modifier: 'short-path',
  },
];

export const MAPS: MapDef[] = MAP_SOURCES.map(compileMap);
